import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, Eye, X, Pencil } from 'lucide-react';
import EditApplicantModal from '../../../pages/Admin/Applicants/components/EditApplicantModal';
import AddApplicantModal from '../../../pages/Admin/Applicants/components/AddApplicantModal';
import EnhancedApplicantsTable from './EnhancedApplicantsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedApplicants.css';
import { supabaseApplicantService } from '../../../services/supabase/applicants';
import type { Applicant, ApplicantFilter } from '../../../types/applicant';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import ApplicantDetailsModal from '../../../components/Applicants/ApplicantDetailsModal';
import { useApplicantFilters } from '../../../hooks/useApplicantFilters';

// Define the sort field type
type ApplicantSortField = 'appl_id' | 'appl_name' | 'appl_cont_name' | 'appl_email';

// Define the sort configuration type
interface SortConfig {
  field: ApplicantSortField;
  direction: 'asc' | 'desc';
}

export default function EnhancedApplicants() {
  const queryClient = useQueryClient();
  const { canEditLeases, isAdmin, canManageSystemSettings } = usePermissions();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editApplicant, setEditApplicant] = useState<Applicant | null>(null);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ApplicantFilter>({
    name: null,
    contactName: null,
    email: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'appl_name',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);

  // Fetch applicant data
  const { data: applicants = [], isLoading, error } = useQuery<Applicant[]>({
    queryKey: ['applicants'],
    queryFn: supabaseApplicantService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Extract unique values for filter dropdowns
  const { names, contactNames, emails } = useMemo(() => {
    const namesSet = new Set<string>();
    const contactNamesSet = new Set<string>();
    const emailsSet = new Set<string>();

    applicants.forEach(applicant => {
      if (applicant.appl_name) namesSet.add(applicant.appl_name);
      if (applicant.appl_cont_name) contactNamesSet.add(applicant.appl_cont_name);
      if (applicant.appl_email) emailsSet.add(applicant.appl_email);
    });

    return {
      names: Array.from(namesSet).sort(),
      contactNames: Array.from(contactNamesSet).sort(),
      emails: Array.from(emailsSet).sort()
    };
  }, [applicants]);

  // Apply filters
  const filteredApplicants = useApplicantFilters(applicants, searchQuery, filters);

  // Apply sorting
  const sortedApplicants = useMemo(() => {
    if (!filteredApplicants) return [];
    
    return [...filteredApplicants].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'appl_id':
          return direction * (a.appl_id - b.appl_id);
        case 'appl_name':
          return direction * a.appl_name.localeCompare(b.appl_name);
        case 'appl_cont_name':
          return direction * ((a.appl_cont_name || '').localeCompare(b.appl_cont_name || ''));
        case 'appl_email':
          return direction * ((a.appl_email || '').localeCompare(b.appl_email || ''));
        default:
          return 0;
      }
    });
  }, [filteredApplicants, sortConfig]);

  // Apply pagination
  const paginatedApplicants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedApplicants.slice(startIndex, endIndex);
  }, [sortedApplicants, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: ApplicantSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<ApplicantFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle edit applicant
  const handleEditClick = (applicant: Applicant) => {
    setEditApplicant(applicant);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['applicants'] });
  };

  // Handle view details
  const handleViewDetails = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
  };

  // Handle remove filter
  const handleRemoveFilter = (key: keyof ApplicantFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedApplicants.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<Applicant>[] = [
    {
      id: 'edit',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Applicant"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    { 
      id: 'view', 
      header: 'View', 
      accessor: (row) => (
        <button
          onClick={() => handleViewDetails(row)}
          className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg"
          title="View Details"
        >
          <Eye className="h-5 w-5" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center' as 'center'
    },
    {
      id: 'appl_name',
      header: 'Name',
      accessor: (row) => (
        <div className="truncate" title={row.appl_name}>
          {formatTextWithLinks(row.appl_name)}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'appl_cont_name',
      header: 'Contact Name',
      accessor: (row) => (
        <div className="truncate" title={row.appl_cont_name || '-'}>
          {formatTextWithLinks(row.appl_cont_name || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'appl_phone',
      header: 'Phone',
      accessor: (row) => (
        <div className="truncate" title={row.appl_phone || '-'}>
          {formatTextWithLinks(row.appl_phone || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth
    },
    {
      id: 'appl_email',
      header: 'Email',
      accessor: (row) => (
        <div className="truncate" title={row.appl_email || '-'}>
          {formatTextWithLinks(row.appl_email || '-')}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'appl_abbr',
      header: 'Abbreviation',
      accessor: (row) => (
        <div className="truncate" title={row.appl_abbr || '-'}>
          {formatTextWithLinks(row.appl_abbr || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth
    },
    {
      id: 'appl_id',
      header: 'ID',
      accessor: (row) => row.appl_id,
      width: 'narrow' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading applicant data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-applicants-container p-4">
      <div className="enhanced-applicants-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Applicants</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Applicant
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search applicants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Applicants: {sortedApplicants.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.name && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Name:</span>
                  <span>{filters.name}</span>
                  <button
                    onClick={() => handleRemoveFilter('name')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.contactName && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Contact Name:</span>
                  <span>{filters.contactName}</span>
                  <button
                    onClick={() => handleRemoveFilter('contactName')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.email && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Email:</span>
                  <span>{filters.email}</span>
                  <button
                    onClick={() => handleRemoveFilter('email')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    name: null,
                    contactName: null,
                    email: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Name filter */}
                <div className="filter-group">
                  <label htmlFor="name-filter">Name</label>
                  <select
                    id="name-filter"
                    value={filters.name || ''}
                    onChange={(e) => handleFilterChange({ name: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All names</option>
                    {names.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Contact Name filter */}
                <div className="filter-group">
                  <label htmlFor="contact-name-filter">Contact Name</label>
                  <select
                    id="contact-name-filter"
                    value={filters.contactName || ''}
                    onChange={(e) => handleFilterChange({ contactName: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All contact names</option>
                    {contactNames.map(contactName => (
                      <option key={contactName} value={contactName}>{contactName}</option>
                    ))}
                  </select>
                </div>
                
                {/* Email filter */}
                <div className="filter-group">
                  <label htmlFor="email-filter">Email</label>
                  <select
                    id="email-filter"
                    value={filters.email || ''}
                    onChange={(e) => handleFilterChange({ email: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All emails</option>
                    {emails.map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-applicants-table-container enhanced-table-force-scroll">
        <EnhancedApplicantsTable
          columns={columns}
          data={paginatedApplicants}
          keyField="appl_id"
          width="1500px"
          maxHeight="calc(100% - 180px)"
          loading={isLoading}
          emptyMessage="No applicants found"
          horizontalScroll={true}
          className="h-full"
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings,
            canEditLeases
          }}
        />
      </div>
      
      {/* Applicant details modal */}
      {selectedApplicant && (
        <ApplicantDetailsModal
          applicant={selectedApplicant}
          onClose={() => setSelectedApplicant(null)}
        />
      )}

      {/* Add Applicant Modal */}
      {isAddModalOpen && (
        <AddApplicantModal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Applicant Modal */}
      {editApplicant && (
        <EditApplicantModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditApplicant(null); // Reset selected applicant when modal is closed
          }}
          onSuccess={handleEditSuccess}
          applicant={editApplicant}
        />
      )}
    </div>
  );
}
